/**
 * The Lesson Summary ticket on Step 1 "Lesson Time" — [BUT-843, ours].
 *
 * The `DemoTicketCard` composition: `TicketCard` + `BookingSummaryDisplay` +
 * `TicketDivider` + `TicketCtaButton`. Instructor and bay names are resolved
 * by the caller and passed in — never read from `useLessonFlow()` directly.
 * `priceSlot` is the direct/hybrid lesson amount ("$85.00 / Taxes not
 * included"), passed by the caller only when the server quote says a card is
 * owed.
 *
 * @module pages/lessons/steps/components/LessonTicketCard
 */

import type { ReactNode } from 'react';
import { format, parseISO } from 'date-fns';
import { TicketCard, TicketDivider } from '@butter/ui/patterns';
import { BookingSummaryDisplay } from '@butter/ui/domains/booking';
import { formatTimeRange } from '@butter/library/datetime';
import { useLessonFlow, formatLessonHours } from '~/services/internalApi/lessons';
import { TicketCtaButton } from '~/pages/booking/shared/TicketCtaButton';

export interface LessonTicketCardProps {
  /** Resolved by the caller (pack → club roster → session sugar), never the raw session value. */
  instructorName: string;
  /** Resolved by the caller from `club.bays`. */
  bayName: string;
  onContinue: () => void;
  canContinue: boolean;
  className?: string;
  /** Price row, left of the CTA. Omitted when nothing is owed or no quote is available (guests). */
  priceSlot?: ReactNode;
}

export function LessonTicketCard({ instructorName, bayName, onContinue, canContinue, className, priceSlot }: LessonTicketCardProps) {
  const { date, startTime, endTime, durationMinutes } = useLessonFlow();

  if (!date || !startTime || !endTime) return null;

  return (
    <TicketCard borderColor="var(--theme-100)" punchRadius="1.3rem" className={className} data-testid="lesson-ticket-card">
      <BookingSummaryDisplay
        title="Lesson Summary"
        instructorName={instructorName}
        bayName={bayName}
        date={format(parseISO(date), 'EEEE, d MMM yyyy')}
        timeRange={formatTimeRange(startTime, endTime)}
        duration={formatLessonHours(durationMinutes ?? 0)}
      />

      {priceSlot && <div className="px-4 pb-6 md:hidden">{priceSlot}</div>}

      <TicketDivider punch className="hidden md:block" />

      <div className="hidden md:flex md:items-center md:justify-end md:gap-10 md:p-6" data-testid="lesson-ticket-card-actions">
        {priceSlot}
        <TicketCtaButton
          variant="desktop"
          label="Continue to Confirmation"
          ariaLabel="Continue to confirmation"
          onClick={onContinue}
          disabled={!canContinue}
          busy={false}
          busyLabel="Processing..."
          dataTestId="lesson-continue-to-confirmation"
        />
      </div>

      <TicketCtaButton
        variant="floating-mobile"
        label="Continue to Confirmation"
        ariaLabel="Continue to confirmation"
        onClick={onContinue}
        disabled={!canContinue}
        busy={false}
        busyLabel="Processing..."
        dataTestId="lesson-continue-to-confirmation-mobile"
      />
    </TicketCard>
  );
}
